import { getSupabase } from "@/lib/supabase";
import type { Session } from "@supabase/supabase-js";
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

type AuthState = {
  session: Session | null;
  user: Session["user"] | null;
  loading: boolean;
};

type AuthContextValue = AuthState & {
  signInWithEmail: (email: string, redirectTo: string) => Promise<{ error: string | null }>;
  signInWithPassword: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    session: null,
    user: null,
    loading: true,
  });

  useEffect(() => {
    const supabase = getSupabase();

    supabase.auth.getSession().then(({ data }) => {
      setState({
        session: data.session,
        user: data.session?.user ?? null,
        loading: false,
      });
    });

    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      setState((prev) => {
        const nextUser = session?.user ?? null;
        // supabase-js refreshes the token whenever a tab regains focus and
        // fires TOKEN_REFRESHED / SIGNED_IN with a brand-new user object that
        // is identical in content. If we hand that new reference downstream,
        // every effect keyed on `user` re-runs - including the route guards
        // (useIsAdmin / useIsContractor), which flip to their loading state
        // and remount the whole subtree, wiping in-progress form state (e.g.
        // a half-filled "New tenancy" form). Keep the previous user object
        // when the id is unchanged so its reference stays stable; only swap
        // it when the user actually changes or their record was updated.
        const keepUser =
          prev.user !== null &&
          nextUser !== null &&
          prev.user.id === nextUser.id &&
          event !== "USER_UPDATED";
        return {
          session,
          user: keepUser ? prev.user : nextUser,
          loading: false,
        };
      });
    });

    return () => data.subscription.unsubscribe();
  }, []);

  const signInWithEmail = useCallback<AuthContextValue["signInWithEmail"]>(
    async (email, redirectTo) => {
      const supabase = getSupabase();
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: redirectTo, shouldCreateUser: false },
      });
      return { error: error?.message ?? null };
    },
    [],
  );

  const signInWithPassword = useCallback<AuthContextValue["signInWithPassword"]>(
    async (email, password) => {
      const supabase = getSupabase();
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      return { error: error?.message ?? null };
    },
    [],
  );

  const signOut = useCallback(async () => {
    const supabase = getSupabase();
    await supabase.auth.signOut();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, signInWithEmail, signInWithPassword, signOut }),
    [state, signInWithEmail, signInWithPassword, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
